import { useAtomValue } from 'jotai'
import { FunctionComponent } from 'react'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import Button from '~/components/Button'
import { Icon } from '~/components/Icon'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { currentScopeState } from '~/state'
import { contactUsUrl } from '~/utils/hubspot'
import { INVENTORY_TYPES, PRODUCT_FAMILIES, PRODUCT_PITCHES, ProductFamily, ProductPitch } from './productPitch'

/** Which half of the catalogue a card belongs to, so the two families never read as one undifferentiated grid. */
const FAMILY_ACCENT: Record<ProductFamily, string> = {
  stack: 'var(--color-king)',
  demand: 'var(--color-pool)',
}

type OffersUpsellPanelProps = {
  /** Which page the request came from, so the two entry points do not share one funnel. */
  trackingLabel: TrackingLabel
}

const ProductCard: FunctionComponent<{ product: ProductPitch }> = ({ product }) => (
  <li className={'flex flex-col gap-2 rounded border border-platinum bg-white p-5 transition-colors hover:border-silver'}>
    <div className={'flex items-center gap-2'}>
      <Icon width={'22px'} name={product.icon} fill={FAMILY_ACCENT[product.family]} />
      <h3 className={'m-0 font-rubik text-base/[26px] font-semibold'}>{product.name}</h3>
    </div>
    <p className={'m-0 text-sm/[22px] font-medium'} style={{ color: FAMILY_ACCENT[product.family] }}>
      {product.tagline}
    </p>
    <p className={'m-0 text-xs/5 text-slate-grey'}>{product.description}</p>
    <ul className={'m-0 mt-1 flex list-none flex-col gap-1.5 p-0'}>
      {product.benefits.map((benefit) => (
        <li key={benefit} className={'flex items-start gap-2 text-xs/5'}>
          <Icon width={'14px'} name={'check'} fill={FAMILY_ACCENT[product.family]} className={'mt-[3px]'} />
          <span>{benefit}</span>
        </li>
      ))}
    </ul>
  </li>
)

/**
 * What a scope that owns nothing sees in place of an Ad Management page — the Ad units tabs, the
 * bidder Catalog, and whatever comes next.
 *
 * Pitching each locked thing separately would make the page read as a list of things the customer cannot
 * have. One page selling the whole catalogue, grouped by the question each half answers — how do I serve
 * my inventory, how do I sell it for more — is the same information as an invitation rather than as a row
 * of locked doors.
 *
 * The kinds of inventory Pubstack can run sit under the title as a single line of chips rather than five
 * cards of their own: they are the breadth claim the products are then chosen within, not a sixth thing
 * to choose between. The products carry the page, and the only ask on it closes it.
 */
export const OffersUpsellPanel: FunctionComponent<OffersUpsellPanelProps> = ({ trackingLabel }) => {
  const currentScope = useAtomValue(currentScopeState)
  const user = useAuthenticatedUser()

  const onContact = () => window.open(contactUsUrl({ email: user.profile.email, scopeName: currentScope.name }), '_blank')

  return (
    <div className={'mx-auto flex w-full max-w-[980px] flex-col gap-8 py-8'}>
      <header className={'flex flex-col items-center gap-3 text-center'}>
        <h1 className={'m-0 max-w-[680px] font-rubik text-[26px]/[36px] font-normal'}>Everything you monetise, in one platform</h1>
        <ul className={'m-0 flex list-none flex-wrap items-center justify-center gap-x-6 gap-y-2 rounded border border-platinum bg-ghost p-3'}>
          {/* Product-neutral on purpose: this panel also stands in for My bidder seats, where a line about
              one ad unit list would be talking about the wrong page. */}
          <li className={'text-xs/5 font-medium text-hurricane'}>Every kind of inventory Pubstack can run</li>
          {INVENTORY_TYPES.map(({ icon, label }) => (
            <li key={label} className={'flex items-center gap-1.5 text-xs/5 text-slate-grey'}>
              <Icon width={'16px'} name={icon} fill={'var(--color-hurricane)'} />
              {label}
            </li>
          ))}
        </ul>
        <p className={'m-0 max-w-[640px] text-base/[26px] text-slate-grey'}>
          Run your whole ad stack, then sell every impression for what it is actually worth. Tell us what your inventory looks like and we will work out which pieces fit.
        </p>
      </header>

      {PRODUCT_FAMILIES.map(({ id, title, subtitle }) => (
        <section key={id} className={'flex flex-col gap-3'}>
          <div className={'flex flex-col gap-0.5'}>
            <h2 className={'m-0 font-rubik text-base/[26px] font-medium'}>{title}</h2>
            <p className={'m-0 text-xs/5 text-slate-grey'}>{subtitle}</p>
          </div>
          <ul className={'m-0 grid list-none grid-cols-1 gap-4 p-0 lg:grid-cols-2'}>
            {PRODUCT_PITCHES.filter((product) => product.family === id).map((product) => (
              <ProductCard key={product.name} product={product} />
            ))}
          </ul>
        </section>
      ))}

      {/* The ask comes after the reading, not before it: the reader has to know what is on offer before
          being asked to start a conversation about it. */}
      <footer className={'flex flex-col items-center gap-2 text-center'}>
        <p className={'m-0 text-xs/5 text-slate-grey'}>Not sure which pieces fit? Tell us what your inventory looks like.</p>
        {/* Not "Contact us": the sidebar footer carries that permanently, opening this same form, and
            the two sat on screen together with only one of them knowing what had just been read. */}
        <Button variant={'primary'} onClick={onContact} trackingLabel={trackingLabel}>
          Talk to us
        </Button>
      </footer>
    </div>
  )
}
